import './NotificationBell.css';

import { BellIcon as BellIconOutline } from '@heroicons/react/24/outline';
import { BellIcon as BellIconSolid } from '@heroicons/react/24/solid';
import {
  AlertTriangle,
  AtSign,
  BellRing,
  Clock,
  FileText,
  Inbox,
  Info,
  MessageSquare,
  UserPlus,
} from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { RainmakerLogo } from '@/components/rainmaker/RainmakerLogo';
import { cn } from '@/lib/utils';
import type {
  NotificationCategory,
  NotificationSchema,
  NotificationTab,
} from '@/services/api/notificationsService';
import {
  useClientConversationsListQuery,
  useNotificationListQuery,
  useNotificationMarkAllReadMutation,
  useNotificationMarkReadMutation,
  useNotificationUnreadCountsQuery,
} from '@/services/api/notificationsService';

interface NotificationBellProps {
  holidayMode?: boolean;
}

export const NotificationBell: React.FC<NotificationBellProps> = ({
  holidayMode = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'messages' | 'mentions' | 'updates' | 'subscribed'>('mentions');
  const [hasNewNotifications, setHasNewNotifications] = useState(false);
  const [viewMode, setViewMode] = useState<'inbox' | 'history'>('inbox');
  const [isTabTransitioning, setIsTabTransitioning] = useState(false);
  const previousTabRef = useRef(activeTab);
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  // Handle tab changes - smooth transition
  const handleTabChange = useCallback((newTab: 'messages' | 'mentions' | 'updates' | 'subscribed') => {
    if (newTab !== previousTabRef.current) {
      setIsTabTransitioning(true);
      setActiveTab(newTab);
      previousTabRef.current = newTab;
      setTimeout(() => setIsTabTransitioning(false), 50);
    }
  }, []);

  // Map UI tab to API tab
  const apiTab: NotificationTab = activeTab;
  const isMessagesTab = activeTab === 'messages';

  // --- Polling strategy ---
  // 1. Unread counts: 3s poll, pauses when tab is hidden (cheap single-query aggregate)
  // 2. Notification list: 30s safety-net poll, but instantly refetches when counts *increase*
  //    (not on decrease — avoids refetch loop when user marks things read)
  // 3. All polls pause when browser tab loses focus to eliminate background load

  const { data: unreadCountsData } = useNotificationUnreadCountsQuery(
    undefined,
    { pollingInterval: 5000, skipPollingIfUnfocused: true },
  );

  // Track previous unread total — only refetch list when count goes UP (new notification)
  const prevUnreadTotalRef = useRef<number | null>(null);
  const currentUnreadTotal = unreadCountsData?.total ?? 0;
  const shouldRefetchList =
    prevUnreadTotalRef.current !== null &&
    currentUnreadTotal > prevUnreadTotalRef.current;
  prevUnreadTotalRef.current = currentUnreadTotal;

  const { data, isLoading: notificationsLoading, refetch: refetchNotifications } = useNotificationListQuery(
    viewMode === 'inbox'
      ? { tab: apiTab, maxReadNotifications: 10, includeCounts: true }
      : { tab: apiTab, includeCounts: true },
    { pollingInterval: 30000, skip: isMessagesTab, skipPollingIfUnfocused: true },
  );

  // Refetch list immediately when new notifications arrive (count increased)
  useEffect(() => {
    if (shouldRefetchList && !isMessagesTab) {
      refetchNotifications();
    }
  }, [shouldRefetchList, isMessagesTab, refetchNotifications]);

  const { data: conversationsData, isLoading: conversationsLoading } = useClientConversationsListQuery(
    { limit: 10 },
    { pollingInterval: 30000, skip: !isMessagesTab, skipPollingIfUnfocused: true },
  );

  const isLoading = isMessagesTab ? conversationsLoading : notificationsLoading;

  const [markRead] = useNotificationMarkReadMutation();
  const [markAllRead] = useNotificationMarkAllReadMutation();

  // Backend returns NotificationListResponseSchema object directly
  // In inbox mode: already filtered to unlimited unread + max 10 read notifications
  // In history mode: all notifications
  // Clear during tab transitions for smooth UX
  const notifications = React.useMemo(
    () => (isTabTransitioning ? [] : data?.notifications || []),
    [data?.notifications, isTabTransitioning],
  );

  // Client conversations for messages tab
  const conversations = React.useMemo(
    () => (isTabTransitioning ? [] : conversationsData?.conversations || []),
    [conversationsData?.conversations, isTabTransitioning],
  );

  // Use the counts from the dedicated endpoint for accurate badge display
  // Only count messages + mentions for the badge — exclude system updates to reduce noise
  const unreadCounts = unreadCountsData || { total: 0, messages: 0, mentions: 0, updates: 0, subscribed: 0 };
  const totalUnreadCount = unreadCounts.messages + unreadCounts.mentions;

  // Total across all categories for solid bell detection
  const allUnreadCount = totalUnreadCount + unreadCounts.subscribed;

  // Check if there are new notifications since last time user opened the bell
  // Uses unread counts (which poll independently) so it works across all tabs
  useEffect(() => {
    if (allUnreadCount > 0) {
      const lastSeenCount = Number(localStorage.getItem('notificationBellLastSeenCount') || '0');
      if (allUnreadCount > lastSeenCount) {
        setHasNewNotifications(true);
      }
    } else {
      setHasNewNotifications(false);
    }
  }, [allUnreadCount]);

  // Open popover on hover
  const handleMouseEnter = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    if (!isOpen) {
      // Opening the bell - save current count so we know when new ones arrive
      localStorage.setItem('notificationBellLastSeenCount', String(allUnreadCount));
      setHasNewNotifications(false);
      setViewMode('inbox');
    }
    setIsOpen(true);
  }, [isOpen]);

  const handleMouseLeave = useCallback(() => {
    hoverTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 150);
  }, []);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Click navigates to notifications page
  const handleBellClick = () => {
    setIsOpen(false);
    navigate('/notifications');
  };

  const displayedNotifications = React.useMemo(() => {
    // In history mode, show all notifications from the current tab
    // In inbox mode, also show all (API handles filtering)
    return notifications;
  }, [notifications]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleNotificationClick = (notification: NotificationSchema) => {
    // Mark as read if unread (read_at is null OR marked_unread is true)
    if (!notification.read_at || notification.marked_unread) {
      markRead({
        id: notification.id,
        markReadSchema: { marked_unread: false },
      });
    }

    // Navigate if deep link exists
    if (notification.deep_link_url) {
      navigate(notification.deep_link_url);
      setIsOpen(false);
    }
  };

  const handleMarkAllRead = () => {
    // Mark all read for the current tab only
    markAllRead({ tab: apiTab });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const diff = new Date().getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 1) return 'Now';
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    return `${days}d`;
  };

  const getIcon = (category: NotificationCategory | null | undefined) => {
    const iconClass = 'w-3 h-3 stroke-2';
    switch (category) {
      case 'SUGGESTION':
        return <RainmakerLogo size={12} />;
      case 'MENTION':
        return <AtSign className={iconClass} />;
      case 'ASSIGNMENT':
        return <UserPlus className={iconClass} />;
      case 'ALERT':
        return <AlertTriangle className={iconClass} />;
      case 'REMINDER':
        return <Clock className={iconClass} />;
      case 'UPDATE':
        return <FileText className={iconClass} />;
      case 'INFO':
        return <Info className={iconClass} />;
      default:
        return <BellIconOutline className={iconClass} />;
    }
  };

  const getIconStyles = () => {
    return 'text-indigo-700 bg-white';
  };

  // Get current tab unread count for the "Mark all read" button
  const getTabUnreadCount = () => {
    if (activeTab === 'messages') return unreadCounts.messages;
    if (activeTab === 'mentions') return unreadCounts.mentions;
    if (activeTab === 'subscribed') return unreadCounts.subscribed;
    return unreadCounts.updates;
  };
  const currentTabUnreadCount = getTabUnreadCount();

  return (
    <div
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Trigger Button */}
      <button
        type="button"
        ref={triggerRef}
        onClick={handleBellClick}
        className={cn(
          `relative group p-2 rounded-full transition-all duration-200 focus:outline-none
          ${isOpen ? 'bg-white/10 text-white shadow-[0_0_10px_rgba(255,255,255,0.1)]' : 'text-white hover:bg-white/5'}`,
          totalUnreadCount === 0 && 'text-gray-400',
        )}
      >
        {/* Use solid bell with inner shadow when there are NEW unread notifications user hasn't seen */}
        {hasNewNotifications && totalUnreadCount > 0 ? (
          <BellIconSolid
            className="w-7 h-7 transition-all duration-200 group-hover:rotate-12"
            style={{
              filter: 'drop-shadow(inset 0 2px 4px rgba(0, 0, 0, 0.3))',
            }}
          />
        ) : (
          <BellIconOutline
            className={`w-7 h-7 transition-all duration-200 ${isOpen ? 'rotate-12 scale-110' : 'group-hover:rotate-12'}`}
          />
        )}

        {/* Badge capsule — joined segments, top-right */}
        {(totalUnreadCount > 0 || unreadCounts.subscribed > 0) && (
          <span className={cn(
            'absolute -top-0.5 flex items-stretch h-[16px] rounded-full overflow-hidden shadow-sm ring-2 ring-[#1e1b4b]',
            totalUnreadCount > 0 && unreadCounts.subscribed > 0 ? '-right-2.5' : '-right-1',
          )}>
            {totalUnreadCount > 0 && (
              <span
                className={cn(
                  'flex items-center justify-center min-w-[16px] px-1 text-[10px] font-bold leading-none',
                  holidayMode
                    ? 'bg-white text-red-700'
                    : 'bg-red-500 text-white',
                )}
              >
                {totalUnreadCount}
              </span>
            )}
            {unreadCounts.subscribed > 0 && (
              <span className="flex items-center justify-center min-w-[16px] px-1 text-[10px] font-bold leading-none bg-amber-400 text-amber-900">
                {unreadCounts.subscribed}
              </span>
            )}
          </span>
        )}
      </button>

      {/* Popover */}
      {isOpen && (
        <div
          ref={popoverRef}
          className="notification-popover-enter absolute right-0 top-full mt-2 w-[320px] bg-white rounded-xl shadow-2xl transform origin-top-right z-50 overflow-hidden border"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-gray-300/30 bg-gray-50">
            <div className="flex items-center gap-1.5">
              {viewMode === 'history' && (
                <button
                  type="button"
                  onClick={() => setViewMode('inbox')}
                  className="text-[10px] font-medium text-gray-600 hover:text-gray-900 mr-0.5"
                >
                  ←
                </button>
              )}
              <span className="text-[11px] font-semibold text-gray-900">
                {viewMode === 'inbox' ? 'Inbox' : 'History'}
              </span>
              {/* Tab Switcher - Messages / Mentions / Updates */}
              {viewMode === 'inbox' && (
                <div className="flex p-0.5 bg-gray-200/50 rounded-lg ml-1">
                  <button
                    type="button"
                    onClick={() => handleTabChange('messages')}
                    className={`flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-medium rounded-[5px] transition-all duration-100 ${activeTab === 'messages' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}
                  >
                    <MessageSquare className="w-2.5 h-2.5" />
                    {unreadCounts.messages > 0 && (
                      <span
                        className={cn(
                          'px-1 text-[8px] font-bold rounded-full',
                          activeTab === 'messages'
                            ? 'bg-indigo-100 text-indigo-700'
                            : 'bg-gray-300 text-gray-600',
                        )}
                      >
                        {unreadCounts.messages}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleTabChange('mentions')}
                    className={`flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-medium rounded-[5px] transition-all duration-100 ${activeTab === 'mentions' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}
                  >
                    <AtSign className="w-2.5 h-2.5" />
                    {unreadCounts.mentions > 0 && (
                      <span
                        className={cn(
                          'px-1 text-[8px] font-bold rounded-full',
                          activeTab === 'mentions'
                            ? 'bg-indigo-100 text-indigo-700'
                            : 'bg-gray-300 text-gray-600',
                        )}
                      >
                        {unreadCounts.mentions}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleTabChange('updates')}
                    className={`flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-medium rounded-[5px] transition-all duration-100 ${activeTab === 'updates' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}
                  >
                    <BellIconOutline className="w-2.5 h-2.5" />
                    {unreadCounts.updates > 0 && (
                      <span
                        className={cn(
                          'px-1 text-[8px] font-bold rounded-full',
                          activeTab === 'updates'
                            ? 'bg-indigo-100 text-indigo-700'
                            : 'bg-gray-300 text-gray-600',
                        )}
                      >
                        {unreadCounts.updates}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleTabChange('subscribed')}
                    className={`flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-medium rounded-[5px] transition-all duration-100 ${activeTab === 'subscribed' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}
                  >
                    <BellRing className="w-2.5 h-2.5" />
                    {unreadCounts.subscribed > 0 && (
                      <span
                        className={cn(
                          'px-1 text-[8px] font-bold rounded-full',
                          activeTab === 'subscribed'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-amber-300 text-amber-700',
                        )}
                      >
                        {unreadCounts.subscribed}
                      </span>
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* Mark all read - only show in inbox mode when there are unread */}
            {viewMode === 'inbox' && currentTabUnreadCount > 0 && (
              <button
                type="button"
                onClick={handleMarkAllRead}
                className="text-[9px] font-medium text-indigo-600 hover:text-indigo-700 transition-colors px-1.5 py-0.5 rounded hover:bg-indigo-50"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-[320px] overflow-y-auto custom-scrollbar">
            {isLoading && (
              <div className="flex items-center justify-center py-12 animate-in fade-in duration-150">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
              </div>
            )}

            {!isLoading && !isMessagesTab && displayedNotifications.length === 0 && (
              <div className="flex flex-col items-center justify-center py-10 text-gray-400 px-4 animate-in fade-in duration-200">
                {activeTab === 'mentions' && (
                  <AtSign className="w-7 h-7 text-gray-200 mb-2 stroke-[1.5]" />
                )}
                {activeTab === 'updates' && (
                  <Inbox className="w-7 h-7 text-gray-200 mb-2 stroke-[1.5]" />
                )}
                {activeTab === 'subscribed' && (
                  <BellRing className="w-7 h-7 text-gray-200 mb-2 stroke-[1.5]" />
                )}
                <p className="text-[10px] font-medium text-gray-400">
                  {activeTab === 'mentions' && 'No mentions'}
                  {activeTab === 'updates' && 'No updates'}
                  {activeTab === 'subscribed' && 'No subscribed notifications'}
                </p>
              </div>
            )}

            {!isLoading && isMessagesTab && conversations.length === 0 && (
              <div className="flex flex-col items-center justify-center py-10 text-gray-400 px-4 animate-in fade-in duration-200">
                <MessageSquare className="w-7 h-7 text-gray-200 mb-2 stroke-[1.5]" />
                <p className="text-[10px] font-medium text-gray-400">No client texts</p>
                <p className="text-[9px] text-gray-300 mt-0.5 text-center">
                  SMS from your matters
                </p>
              </div>
            )}

            {/* Conversations list (messages tab) */}
            {!isLoading && isMessagesTab && conversations.length > 0 && (
              <div className="divide-y divide-gray-200/40 animate-in fade-in duration-200">
                {conversations.map((conversation) => {
                  const hasUnread = conversation.unread_count > 0;
                  const displayName = conversation.client_name || conversation.matter_name || 'Unknown';
                  return (
                    <button
                      key={conversation.matter_id}
                      type="button"
                      onClick={() => {
                        setIsOpen(false);
                        navigate(`/notifications?tab=messages&notification_id=${conversation.matter_id}`);
                      }}
                      className={`group relative flex gap-2 px-2.5 py-2 transition-colors duration-200 w-full text-left
                      ${hasUnread ? 'bg-blue-100/60 hover:bg-blue-100/80' : 'bg-white hover:bg-gray-50'}
                    `}
                    >
                      {/* Icon */}
                      <div className="flex-shrink-0 w-6 h-6 rounded-lg flex items-center justify-center border mt-0.5 text-indigo-700 bg-white">
                        <MessageSquare className="w-3 h-3 stroke-2" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-1.5">
                          <h4
                            className={`text-[10px] leading-tight truncate ${hasUnread ? 'font-semibold text-gray-900' : 'font-medium text-gray-600'}`}
                          >
                            {displayName}
                          </h4>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <span className="text-[8px] text-gray-400 tabular-nums font-medium">
                              {formatTime(conversation.latest_message_timestamp)}
                            </span>
                            {hasUnread && (
                              <span className="w-1.5 h-1.5 rounded-full bg-indigo-600" />
                            )}
                          </div>
                        </div>

                        <p
                          className={`text-[10px] leading-snug mt-0.5 line-clamp-2 ${hasUnread ? 'text-gray-800 font-medium' : 'text-gray-500'}`}
                        >
                          {conversation.latest_message_preview}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Notifications list (mentions/updates tabs) */}
            {!isLoading && !isMessagesTab && displayedNotifications.length > 0 && (
              <div className="divide-y divide-gray-200/40 animate-in fade-in duration-200">
                {displayedNotifications.map((notification) => {
                  const isUnread =
                    !notification.read_at || notification.marked_unread;
                  return (
                    <div
                      key={notification.id}
                      className={`group relative flex gap-2 px-2.5 py-2 transition-colors duration-200 w-full
                      ${isUnread ? 'bg-blue-100/60 hover:bg-blue-100/80' : 'bg-white hover:bg-gray-50'}
                    `}
                    >
                      {/* Mark Read/Unread Button - appears on hover (only in inbox mode) */}
                      {viewMode === 'inbox' && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            markRead({
                              id: notification.id,
                              markReadSchema: {
                                marked_unread: !isUnread,
                              },
                            });
                          }}
                          className="absolute right-1.5 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 px-1.5 py-0.5 rounded bg-white hover:bg-gray-100 text-gray-600 hover:text-gray-900 text-[9px] font-medium shadow-sm border border-gray-200"
                        >
                          {isUnread ? 'Read' : 'Unread'}
                        </button>
                      )}
                      {/* Clickable area for navigation */}
                      <button
                        type="button"
                        onClick={() => handleNotificationClick(notification)}
                        className="flex gap-2 flex-1 min-w-0 text-left cursor-pointer"
                      >
                        {/* Icon */}
                        <div
                          className={`flex-shrink-0 w-6 h-6 rounded-lg flex items-center justify-center border mt-0.5 ${getIconStyles()}`}
                        >
                          {getIcon(notification.category)}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-1.5">
                            <h4
                              className={`text-[10px] leading-tight truncate ${isUnread ? 'font-semibold text-gray-600' : 'font-medium text-gray-500'}`}
                            >
                              {notification.title}
                            </h4>
                            <span className="text-[8px] text-gray-400 flex-shrink-0 tabular-nums font-medium">
                              {formatTime(notification.created_at)}
                            </span>
                          </div>

                          {notification.description && (
                            <p
                              className={`text-[10px] leading-snug mt-0.5 ${isUnread ? 'text-gray-900 font-medium' : 'text-gray-600'} line-clamp-2`}
                            >
                              {notification.description}
                            </p>
                          )}
                          {notification.matter_name && (
                            <p className="text-[9px] text-indigo-600 bg-indigo-50 font-medium truncate mt-1 px-1 py-0.5 w-fit border border-indigo-200/40 rounded">
                              {notification.matter_name}
                            </p>
                          )}
                        </div>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer - View All Notifications - only show in inbox mode */}
          {viewMode === 'inbox' && (
            <div className="border-t border-gray-200/40 bg-gray-50/50">
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  navigate('/notifications');
                }}
                className="w-full px-2.5 py-2 text-[10px] font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50/50 transition-colors text-center"
              >
                View all →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
